import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import './CockpitDashboard.css';

// 商务蓝色系配色
const COCKPIT_COLORS = [
  '#2563eb', '#3b82f6', '#60a5fa', '#06b6d4', '#0891b2',
  '#0e7490', '#155e75', '#164e63', '#1e40af', '#1d4ed8'
];

function getToday() {
  return new Date().toISOString().split('T')[0];
}

// 实时时钟组件
function LiveClock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const weekDays = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
  const year = time.getFullYear();
  const month = String(time.getMonth() + 1).padStart(2, '0');
  const day = String(time.getDate()).padStart(2, '0');
  const weekDay = weekDays[time.getDay()];
  const hours = String(time.getHours()).padStart(2, '0');
  const minutes = String(time.getMinutes()).padStart(2, '0');
  const seconds = String(time.getSeconds()).padStart(2, '0');

  return (
    <div className="cockpit-clock">
      <span className="clock-date">{year}年{month}月{day}日</span>
      <span className="clock-weekday">{weekDay}</span>
      <span className="clock-time">{hours}:{minutes}:{seconds}</span>
    </div>
  );
}

// KPI翻牌数字组件
function FlipNumber({ value, suffix = '', prefix = '', color = '#f1f5f9' }) {
  const [displayValue, setDisplayValue] = useState(0);
  const prevValue = useRef(0);

  useEffect(() => {
    const start = prevValue.current;
    const end = value;
    const duration = 800;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(start + (end - start) * eased);
      setDisplayValue(current);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
    prevValue.current = end;
  }, [value]);

  return (
    <span className="flip-number" style={{ color }}>
      {prefix}{displayValue.toLocaleString()}{suffix}
    </span>
  );
}

// 可搜索项目选择
function ProjectSelect({ projects, value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const wrapperRef = useRef(null);

  const filteredProjects = useMemo(() => {
    const list = searchText
      ? projects.filter(p => p.toLowerCase().includes(searchText.toLowerCase()))
      : projects;
    return ['all', ...list.filter(p => p !== 'all')];
  }, [projects, searchText]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayText = value === 'all' ? '全部项目' : value;

  return (
    <div className="cockpit-select" ref={wrapperRef}>
      <div className="cockpit-select-trigger" onClick={() => setIsOpen(!isOpen)}>
        <span>{displayText}</span>
        <span className="select-arrow">{isOpen ? '▲' : '▼'}</span>
      </div>
      {isOpen && (
        <div className="cockpit-select-dropdown">
          <input
            type="text"
            placeholder="搜索项目..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            autoFocus
          />
          <div className="cockpit-select-list">
            {filteredProjects.map((project) => (
              <div
                key={project}
                className={`cockpit-select-option ${project === value ? 'active' : ''}`}
                onClick={() => { onChange(project); setIsOpen(false); setSearchText(''); }}
              >
                {project === 'all' ? '全部项目' : project}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function WorkorderDashboard() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('all');
  const [period, setPeriod] = useState('year');
  const [customMode, setCustomMode] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(getToday);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [serviceTab, setServiceTab] = useState('overview');
  const [drillModal, setDrillModal] = useState(null);
  const [drillData, setDrillData] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  useEffect(() => {
    fetch('/api/workorder/projects')
      .then(r => r.json())
      .then(d => { if (d.success) setProjects(d.projects); })
      .catch(() => {});
  }, []);

  const buildQueryParams = () => {
    const params = new URLSearchParams();
    params.append('project', selectedProject);
    if (customMode) {
      params.append('startDate', startDate);
      params.append('endDate', endDate);
    } else {
      params.append('period', period);
    }
    return params.toString();
  };

  const fetchData = () => {
    setLoading(true);
    fetch(`/api/workorder/dashboard?${buildQueryParams()}`)
      .then(r => r.json())
      .then(d => { 
        console.log('Dashboard API response:', d);
        if (d.success) setData(d.data); 
      })
      .catch(err => console.error('Fetch error:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [selectedProject, period, customMode, startDate, endDate]);

  const handleDrillDown = (dimension, value) => {
    setDrillModal({ dimension, value });
    setDrillData([]);
    setDrillLoading(true);
    const params = new URLSearchParams();
    params.append('project', selectedProject);
    if (customMode) {
      params.append('startDate', startDate);
      params.append('endDate', endDate);
    } else {
      params.append('period', period);
    }
    if (dimension !== 'all') {
      params.append('dimension', dimension);
      params.append('value', value);
    }
    fetch(`/api/workorder/detail?${params.toString()}`)
      .then(r => r.json())
      .then(d => { if (d.success) setDrillData(d.data); })
      .catch(() => setDrillData([]))
      .finally(() => setDrillLoading(false));
  };

  const handleExport = () => {
    if (!drillModal) return;
    const { dimension, value } = drillModal;
    const params = new URLSearchParams();
    params.append('project', selectedProject);
    if (customMode) {
      params.append('startDate', startDate);
      params.append('endDate', endDate);
    } else {
      params.append('period', period);
    }
    if (dimension !== 'all') {
      params.append('dimension', dimension);
      params.append('value', value);
    }
    window.open(`/api/workorder/export?${params.toString()}`, '_blank');
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handleRefresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    fetch('/api/workorder/refresh', { method: 'POST' })
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setLastRefresh(new Date().toLocaleString());
          // 刷新完成后重新加载看板数据
          fetchData();
        }
      })
      .catch(err => console.error('Refresh error:', err))
      .finally(() => setRefreshing(false));
  };

  // ECharts配置 - 简化版
  const typePieOption = useMemo(() => {
    if (!data?.typeDistribution) return {};
    return {
      backgroundColor: 'transparent',
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      legend: { 
        orient: 'vertical', 
        right: 10, 
        top: 'center', 
        textStyle: { color: '#8899aa', fontSize: 11 },
        formatter: (name) => {
          const item = data.typeDistribution.find(d => d.name === name);
          return item ? `${name}  ${item.value}` : name;
        }
      },
      series: [{
        type: 'pie',
        radius: ['45%', '70%'],
        center: ['35%', '50%'],
        label: { show: false },
        data: data.typeDistribution.map((item, i) => ({ ...item, itemStyle: { color: COCKPIT_COLORS[i % COCKPIT_COLORS.length] } }))
      }]
    };
  }, [data]);

  const trendOption = useMemo(() => {
    if (!data?.yearOverYear?.trend) return {};
    const trendData = data.yearOverYear.trend;
    const labels = trendData.map(m => m.label);
    const currentData = trendData.map(m => m.current);
    const lastYearData = trendData.map(m => m.lastYear);
    
    // 根据数据量调整标签显示
    const labelInterval = labels.length > 20 ? Math.floor(labels.length / 10) : 0;
    const labelRotate = labels.length > 15 ? 45 : 0;
    
    return {
      backgroundColor: 'transparent',
      tooltip: { trigger: 'axis' },
      legend: { data: ['今年', '去年'], textStyle: { color: '#8899aa' }, top: 5 },
      grid: { left: 45, right: 25, top: 45, bottom: 35 },
      xAxis: { 
        type: 'category', 
        data: labels, 
        axisLine: { lineStyle: { color: '#334155' } },
        axisLabel: { 
          color: '#64748b', 
          fontSize: 10,
          rotate: labelRotate,
          interval: labelInterval
        }
      },
      yAxis: { type: 'value', splitLine: { lineStyle: { color: '#334155', type: 'dashed' } }, axisLabel: { color: '#64748b', fontSize: 10 } },
      series: [
        { 
          name: '今年', 
          type: 'bar', 
          data: currentData,
          barWidth: '45%',
          itemStyle: { 
            borderRadius: [4, 4, 0, 0],
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: '#2563eb' },
              { offset: 1, color: '#0088cc' }
            ])
          },
          label: { 
            show: true, 
            position: 'top',
            color: '#2563eb',
            fontSize: 10,
            formatter: (p) => p.value > 0 ? p.value : ''
          }
        },
        { 
          name: '去年', 
          type: 'line', 
          data: lastYearData, 
          smooth: true,
          lineStyle: { color: '#f59e0b', width: 2 }, 
          itemStyle: { color: '#f59e0b' },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(255,215,0,0.2)' },
              { offset: 1, color: 'rgba(255,215,0,0)' }
            ])
          }
        }
      ]
    };
  }, [data]);

  const hotspotOption = useMemo(() => {
    if (!data?.hotspots) return {};
    const items = [...data.hotspots].reverse();
    return {
      backgroundColor: 'transparent',
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: 90, right: 40, top: 10, bottom: 10 },
      xAxis: { type: 'value', splitLine: { lineStyle: { color: '#334155', type: 'dashed' } }, axisLabel: { color: '#64748b' } },
      yAxis: { type: 'category', data: items.map(i => i.name), axisLine: { lineStyle: { color: '#334155' } }, axisLabel: { color: '#94a3b8', fontSize: 11 } },
      series: [{ type: 'bar', data: items.map((item, i) => ({ value: item.value, itemStyle: { color: COCKPIT_COLORS[i % COCKPIT_COLORS.length] } })), label: { show: true, position: 'right', color: '#94a3b8' } }]
    };
  }, [data]);

  const servicePieOption = useMemo(() => {
    if (!data?.serviceDistribution) return {};
    const total = data.serviceDistribution.reduce((sum, item) => sum + item.value, 0);
    return {
      backgroundColor: 'transparent',
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      series: [{
        type: 'pie',
        radius: ['35%', '65%'],
        center: ['50%', '50%'],
        roseType: 'area',
        label: {
          show: true,
          formatter: (params) => {
            const pct = total > 0 ? ((params.value / total) * 100).toFixed(1) : 0;
            return `${params.name}  ${pct}%`;
          },
          color: '#94a3b8',
          fontSize: 11
        },
        data: data.serviceDistribution.map((item, i) => ({ ...item, itemStyle: { color: COCKPIT_COLORS[i % COCKPIT_COLORS.length] } }))
      }]
    };
  }, [data]);

  const serviceDetailOption = useMemo(() => {
    const serviceMap = { '系统调整': '系统调整', '答疑': '答疑', '其他': '其他' };
    const filterKey = serviceMap[serviceTab];
    const detailDist = data?.serviceDetailDistribution || {};
    const items = detailDist[filterKey] || [];
    if (!filterKey || !items.length) return {};
    const total = items.reduce((sum, item) => sum + item.value, 0);
    return {
      backgroundColor: 'transparent',
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: 80, right: 50, top: 10, bottom: 10 },
      xAxis: { type: 'value', splitLine: { lineStyle: { color: '#334155', type: 'dashed' } }, axisLabel: { color: '#64748b' } },
      yAxis: { type: 'category', data: items.map(s => s.name).reverse(), axisLabel: { color: '#94a3b8', fontSize: 10, width: 70, overflow: 'truncate' } },
      series: [{ 
        type: 'bar', 
        data: items.map(s => s.value).reverse(), 
        itemStyle: { color: '#10b981' },
        label: { 
          show: true, 
          position: 'right', 
          color: '#94a3b8',
          formatter: (params) => {
            const percent = total > 0 ? ((params.value / total) * 100).toFixed(1) : 0;
            return `${percent}%`;
          }
        } 
      }]
    };
  }, [data, serviceTab]);

  // 是否休息日 - 南丁格尔玫瑰图
  const workdayCompareOption = useMemo(() => {
    if (!data?.workdayDistribution) return {};
    const wd = data.workdayDistribution.find(d => d.name === '工作日') || { value: 0, closed: 0 };
    const rd = data.workdayDistribution.find(d => d.name === '休息日') || { value: 0, closed: 0 };
    const total = wd.value + rd.value;
    
    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          const pct = total > 0 ? ((params.value / total) * 100).toFixed(1) : 0;
          return `${params.name}<br/>数量: ${params.value}单<br/>占比: ${pct}%`;
        }
      },
      series: [{
        type: 'pie',
        radius: [20, 100],
        center: ['50%', '50%'],
        roseType: 'area',
        itemStyle: {
          borderRadius: 8,
          borderColor: 'rgba(10,25,50,0.8)',
          borderWidth: 2
        },
        label: {
          show: true,
          formatter: (params) => {
            const pct = total > 0 ? ((params.value / total) * 100).toFixed(1) : 0;
            return `${params.name}\n${params.value}单 (${pct}%)`;
          },
          color: '#94a3b8',
          fontSize: 12,
          lineHeight: 18
        },
        labelLine: {
          lineStyle: { color: '#445566' },
          smooth: 0.2,
          length: 10,
          length2: 20
        },
        data: [
          { 
            value: wd.value, 
            name: '工作日',
            itemStyle: { 
              color: new echarts.graphic.LinearGradient(0, 0, 1, 1, [
                { offset: 0, color: '#2563eb' },
                { offset: 1, color: '#0066cc' }
              ])
            }
          },
          { 
            value: rd.value, 
            name: '休息日',
            itemStyle: { 
              color: new echarts.graphic.LinearGradient(0, 0, 1, 1, [
                { offset: 0, color: '#f59e0b' },
                { offset: 1, color: '#ff8c00' }
              ])
            }
          }
        ]
      }]
    };
  }, [data]);

  const unitOption = useMemo(() => {
    if (!data?.unitDistribution) return {};
    // 只显示前10个单位
    const items = [...data.unitDistribution].slice(0, 10).reverse();
    return {
      backgroundColor: 'transparent',
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: 140, right: 40, top: 10, bottom: 10 },
      xAxis: { type: 'value', splitLine: { lineStyle: { color: '#334155', type: 'dashed' } }, axisLabel: { color: '#64748b' } },
      yAxis: { type: 'category', data: items.map(i => i.name), axisLabel: { color: '#94a3b8', fontSize: 10 } },
      series: [{ type: 'bar', data: items.map((item, i) => ({ value: item.value, itemStyle: { color: COCKPIT_COLORS[i % COCKPIT_COLORS.length] } })), label: { show: true, position: 'right', color: '#94a3b8' }, barMaxWidth: 20 }]
    };
  }, [data]);

  // 固定图表高度为240px，与模块图表一致
  const unitChartHeight = 240;

  // 工单来源分析
  const sourceOption = useMemo(() => {
    if (!data?.sourceDistribution) return {};
    const total = data.sourceDistribution.reduce((sum, item) => sum + item.value, 0);
    return {
      backgroundColor: 'transparent',
      tooltip: { 
        trigger: 'item', 
        formatter: (params) => `${params.name}<br/>数量: ${params.value}<br/>占比: ${((params.value / total) * 100).toFixed(1)}%`
      },
      series: [{
        type: 'pie',
        radius: ['40%', '70%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: true,
        itemStyle: {
          borderRadius: 6,
          borderColor: 'rgba(10,25,50,0.8)',
          borderWidth: 2
        },
        label: {
          show: true,
          formatter: (params) => `${params.name}\n${((params.value / total) * 100).toFixed(1)}%`,
          color: '#94a3b8',
          fontSize: 11,
          lineHeight: 16
        },
        labelLine: {
          lineStyle: { color: '#445566' }
        },
        data: data.sourceDistribution.map((item, i) => ({ 
          ...item, 
          itemStyle: { color: COCKPIT_COLORS[i % COCKPIT_COLORS.length] } 
        }))
      }]
    };
  }, [data]);

  const gaugeOption = useMemo(() => {
    if (!data?.summary) return {};
    return {
      backgroundColor: 'transparent',
      series: [{
        type: 'gauge',
        startAngle: 200, endAngle: -20, min: 0, max: 100,
        radius: '100%', center: ['50%', '55%'],
        progress: { show: true, width: 10, itemStyle: { color: '#10b981' } },
        axisLine: { lineStyle: { width: 10, color: [[1, '#334155']] } },
        axisTick: { show: false }, splitLine: { show: false }, axisLabel: { show: false },
        pointer: { show: false }, anchor: { show: false }, title: { show: false },
        detail: { valueAnimation: true, fontSize: 18, fontWeight: 'bold', color: '#10b981', offsetCenter: [0, '5%'], formatter: '{value}%' },
        data: [{ value: data.summary.closeRate }]
      }]
    };
  }, [data]);

  if (!data && !loading) {
    return (
      <div className="cockpit-container">
        <div className="cockpit-empty"><p>请选择项目并加载数据</p></div>
      </div>
    );
  }

  return (
    <div className="cockpit-container">
      {/* 顶部栏 */}
      <div className="cockpit-header">
        <div className="header-left">
          <h1 className="cockpit-title">
            <span className="title-icon">◈</span>
            工单分析看板
            <span className="title-icon">◈</span>
          </h1>
        </div>
        <div className="header-center"><LiveClock /></div>
        <div className="header-right">
          <ProjectSelect projects={projects} value={selectedProject} onChange={setSelectedProject} />
          <div className="cockpit-period-btns">
            {[
              { key: 'today', label: '今日' },
              { key: 'week', label: '本周' },
              { key: 'month', label: '本月' },
              { key: 'quarter', label: '本季' },
              { key: 'year', label: '本年' }
            ].map(p => (
              <button key={p.key} className={`period-btn ${period === p.key && !customMode ? 'active' : ''}`} onClick={() => { setPeriod(p.key); setCustomMode(false); }}>
                {p.label}
              </button>
            ))}
          </div>
          <button className="fullscreen-btn" onClick={toggleFullscreen}>{isFullscreen ? '⊡' : '⊞'}</button>
        </div>
      </div>

      {/* KPI 综合面板 */}
      <div className="kpi-dashboard-panel">
        <div className="kpi-square-card kpi-clickable" onClick={() => handleDrillDown('all', '')}>
          <div className="kpi-label">工单总量</div>
          <div className="kpi-value-large"><FlipNumber value={data?.summary?.total || 0} /></div>
          {data?.yearOverYear?.change?.total && (
            <span className={`kpi-trend-inline ${data.yearOverYear.change.total.value >= 0 ? 'up' : 'down'}`}>
              {data.yearOverYear.change.total.value >= 0 ? '↑' : '↓'}{Math.abs(data.yearOverYear.change.total.percent)}%
            </span>
          )}
          <div className="kpi-click-hint">点击穿透明细</div>
        </div>
        <div className="kpi-square-card">
          <div className="kpi-label">关闭率</div>
          <div className="kpi-value-large"><FlipNumber value={data?.summary?.closeRate || 0} suffix="%" /></div>
        </div>
        <div className="kpi-square-card kpi-compare-card">
          <div className="kpi-label">同比</div>
          <div className="kpi-year-compare">
            <div className="year-row">
              <span className="year-label">今年</span>
              <div className="year-bar-wrapper">
                <div className="year-bar current" style={{ width: `${data?.yearOverYear ? (data.summary.total / Math.max(data.summary.total, data.yearOverYear.lastYear.total) * 100) : 100}%` }}></div>
              </div>
              <span className="year-num">{data?.summary?.total || 0}</span>
            </div>
            <div className="year-row">
              <span className="year-label">去年</span>
              <div className="year-bar-wrapper">
                <div className="year-bar lastyear" style={{ width: `${data?.yearOverYear ? (data.yearOverYear.lastYear.total / Math.max(data.summary.total, data.yearOverYear.lastYear.total) * 100) : 0}%` }}></div>
              </div>
              <span className="year-num">{data?.yearOverYear?.lastYear?.total || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* 第一排：趋势对比（大）+ 服务类型分析（小） */}
      <div className="cockpit-charts-row cockpit-row-2-1">
        <div className="chart-panel">
          <div className="panel-title">趋势对比</div>
          <ReactECharts option={trendOption} style={{ height: '240px' }} />
        </div>
        <div className="chart-panel">
          <div className="panel-title">
            {serviceTab === 'overview' ? '服务类型分析' : serviceTab}
            <div className="service-tabs">
              {[{ key: 'overview', label: '总览' }, { key: '系统调整', label: '系统调整' }, { key: '答疑', label: '答疑' }, { key: '其他', label: '其他' }].map(tab => (
                <button key={tab.key} className={`service-tab ${serviceTab === tab.key ? 'active' : ''}`} onClick={() => setServiceTab(tab.key)}>{tab.label}</button>
              ))}
            </div>
          </div>
          {serviceTab === 'overview' ? (
            <ReactECharts option={servicePieOption} style={{ height: '240px' }} notMerge={true} />
          ) : (
            <ReactECharts option={serviceDetailOption} style={{ height: '240px' }} notMerge={true} />
          )}
        </div>
      </div>

      {/* 第二排：所属模块分析（大）+ 工单类型分布（小） */}
      <div className="cockpit-charts-row cockpit-row-2-1">
        <div className="chart-panel">
          <div className="panel-title">所属模块分析</div>
          <ReactECharts option={hotspotOption} style={{ height: '240px' }} onEvents={{ click: (params) => handleDrillDown('module', params.name) }} />
        </div>
        <div className="chart-panel">
          <div className="panel-title">工单类型分布</div>
          <ReactECharts option={typePieOption} style={{ height: '240px' }} />
        </div>
      </div>

      {/* 第三排：所属单位排行（大）+ 是否休息日（小） */}
      <div className="cockpit-charts-row cockpit-row-2-1">
        <div className="chart-panel">
          <div className="panel-title">所属单位排行</div>
          <ReactECharts option={unitOption} style={{ height: `${unitChartHeight}px` }} onEvents={{ click: (params) => handleDrillDown('unit', params.name) }} />
        </div>
        <div className="chart-panel">
          <div className="panel-title">是否休息日</div>
          <ReactECharts option={workdayCompareOption} style={{ height: '240px' }} />
        </div>
      </div>

      {/* 底部操作区 */}
      <div className="cockpit-footer" style={{ gap: '12px' }}>
        <button className="qa-entry-btn" onClick={() => window.location.href = '/qa-stats'}>
          📊 QA统计
        </button>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="qa-entry-btn"
          style={{ opacity: refreshing ? 0.6 : 1 }}
        >
          {refreshing ? '🔄 刷新中...' : '🔄 刷新数据'}
        </button>
      </div>
      {lastRefresh && (
        <div style={{ textAlign: 'center', fontSize: '11px', color: '#64748b', paddingBottom: '12px' }}>
          上次刷新: {lastRefresh}
        </div>
      )}

      {/* 穿透弹窗 */}
      {drillModal && (
        <div className="cockpit-modal-overlay" onClick={() => setDrillModal(null)}>
          <div className="cockpit-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{drillModal.dimension === 'all' ? '全部工单明细' : `${drillModal.dimension} - ${drillModal.value}`}</h3>
              <div className="modal-actions">
                <button className="modal-btn export-btn" onClick={handleExport}>导出Excel</button>
                <button className="modal-btn close-btn" onClick={() => setDrillModal(null)}>✕</button>
              </div>
            </div>
            <div className="modal-body">
              {drillLoading ? (
                <div className="loading">加载中...</div>
              ) : (
                <div className="drill-table-wrapper">
                  <table className="drill-table">
                    <thead>
                      <tr>
                        <th>工单编号</th><th>工单类型</th><th>服务类型</th><th>对应模块</th><th>问题描述</th><th>解决方案</th><th>状态</th><th>提出时间</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drillData.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item['工单编号']}</td>
                          <td>{item['工单类型']}</td>
                          <td>{item['服务类型']}</td>
                          <td>{item['对应模块']}</td>
                          <td className="desc-cell">{item['工单描述'] || ''}</td>
                          <td className="desc-cell">{item['解决方案'] || ''}</td>
                          <td><span className={`status-badge ${item['是否关闭'] === '是' ? 'closed' : 'open'}`}>{item['是否关闭'] === '是' ? '已关闭' : '未关闭'}</span></td>
                          <td>{(item['工单提出时间'] || '').substring(0, 10)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WorkorderDashboard;
